import { useState } from 'react';
import { useSharedChat } from '../../hooks/useAgentContext';
import { getConversation } from '../../services/agentApi';
import MessageList from '../../components/Agent/MessageList';
import MessageInput from '../../components/Agent/MessageInput';
import ConversationHistory from '../../components/Agent/ConversationHistory';
import { FMEPageHeader, useModelContext } from '../../components/Layout';

export default function AI() {
    const { model } = useModelContext();
    const { messages, isLoading, isThinking, isAvailable, conversationId, autoConfirm, setAutoConfirm, sendMessage, handleConfirmation, stopGeneration, clearChat, loadConversation } = useSharedChat();
    const [showHistory, setShowHistory] = useState(false);

    async function handleSelectConversation(selectedId: string) {
        try {
            const detail = await getConversation(selectedId);
            loadConversation(detail.conversation_id, detail.messages);
            setShowHistory(false);
        } catch (err) {
            console.error('Failed to load conversation:', err);
        }
    }

    function handleNewChat() {
        clearChat();
        setShowHistory(false);
    }

    return (
        <div className="flex flex-col h-full">
            <FMEPageHeader
                title="Prisme AI"
                breadcrumb={[model.name]}
                description="AI-powered insights on Peerception's private network. Your data never leaves our infrastructure."
                titleExtra={isAvailable !== null ? (
                    <div className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${isAvailable ? (isLoading ? 'bg-green-500 animate-pulse-strong' : 'bg-green-500') : 'bg-slate-400'}`} />
                ) : undefined}
                actions={
                    <div className="flex items-center gap-2">
                        {!showHistory && (
                            <button
                                onClick={() => setAutoConfirm(!autoConfirm)}
                                className={`text-xs px-2 py-0.5 rounded border transition-colors cursor-pointer ${
                                    autoConfirm
                                        ? 'text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100 hover:border-amber-300'
                                        : 'text-slate-400 bg-slate-50 border-slate-200 hover:bg-slate-100 hover:border-slate-300'
                                }`}
                                title={autoConfirm ? 'Click to disable auto-confirm' : 'Click to enable auto-confirm'}
                            >
                                {autoConfirm ? 'Auto-confirm on' : 'Auto-confirm off'}
                            </button>
                        )}
                        <button
                            onClick={handleNewChat}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                            title="New chat"
                        >
                            <span className="material-symbols-outlined text-[18px]">add</span>
                            New chat
                        </button>
                        <button
                            onClick={() => setShowHistory(!showHistory)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                                showHistory
                                    ? 'text-primary bg-primary/10'
                                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                            }`}
                            title={showHistory ? 'Back to chat' : 'Conversation history'}
                        >
                            <span className="material-symbols-outlined text-[18px]">history</span>
                            History
                        </button>
                    </div>
                }
            />

            {/* Content area */}
            <div className="flex-1 min-h-0 flex flex-col max-w-4xl mx-auto w-full">
                {showHistory ? (
                    <ConversationHistory
                        onSelect={handleSelectConversation}
                        onClose={() => setShowHistory(false)}
                        currentConversationId={conversationId}
                    />
                ) : (
                    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                        {/* Top spacer — centers content when empty, collapses when messages */}
                        <div className={`basis-0 shrink-0 transition-[flex-grow] duration-700 ease-out ${
                            messages.length === 0 ? 'grow' : 'grow-0'
                        }`} />

                        {/* Welcome text — fades & collapses */}
                        <div className={`shrink-0 text-center px-6 transition-all duration-500 ease-out overflow-hidden ${
                            messages.length === 0
                                ? 'opacity-100 max-h-40 mb-6'
                                : 'opacity-0 max-h-0 mb-0'
                        }`}>
                            <div className="text-slate-300 mb-4">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={0.8}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                            </div>
                            <p className="text-lg font-medium text-slate-600">How can I help you?</p>
                            {/* <p className="text-sm text-slate-400 mt-1">Ask me about your financial model</p> */}
                        </div>

                        {/* Messages area — grows when messages arrive */}
                        <div className={`min-h-0 flex flex-col overflow-hidden transition-[flex-grow] duration-700 ease-out ${
                            messages.length > 0 ? 'grow' : 'grow-0'
                        }`}>
                            {messages.length > 0 && (
                                <MessageList
                                    messages={messages}
                                    isLoading={isLoading}
                                    isThinking={isThinking}
                                    onConfirm={(toolCallId) => handleConfirmation(toolCallId, true)}
                                    onConfirmAll={(toolCallId) => {
                                        setAutoConfirm(true);
                                        handleConfirmation(toolCallId, true);
                                    }}
                                    onCancel={(toolCallId) => handleConfirmation(toolCallId, false)}
                                />
                            )}
                        </div>

                        {/* Input — always rendered, same style */}
                        <MessageInput
                            onSend={sendMessage}
                            disabled={isLoading}
                            isLoading={isLoading}
                            onStop={stopGeneration}
                        />

                        {/* Bottom spacer — larger than top to push content upward */}
                        <div className={`basis-0 shrink-0 transition-[flex-grow] duration-700 ease-out ${
                            messages.length === 0 ? 'grow-[2]' : 'grow-0'
                        }`} />
                    </div>
                )}
            </div>
        </div>
    );
}
